<template>
  <div class="connection-error">
    <div class="card">
      <h1 class="title">通信に時間がかかっています</h1>
      <p class="desc">
        ログイン情報やご契約の確認が、時間内に終わりませんでした。<br />
        通信状態のよい場所で、もう一度お試しください。
      </p>
      <p class="note">ログインし直す必要はありません。</p>
      <div class="actions">
        <button type="button" class="btn btn-primary" :disabled="retrying" @click="retry">
          {{ retrying ? '確認しています...' : 'もう一度試す' }}
        </button>
        <button type="button" class="btn btn-secondary" :disabled="retrying" @click="reload">
          ページを再読み込みする
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * 回復画面（指示書 A-1②）。ルーターのガードが期限内に終わらなかったときの出口。
 * この画面自体は、認証・契約・LINEの確認をしない（同じ確認で再び失敗しないように、ガードは先に通す）。
 * 保護されたデータは表示しない。「もう一度試す」は、元の行き先へ遷移し直して、ガードの確認をやり直す。
 */
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { safeInternalReturnPath } from '../utils/guardDeadline.js'
import { abandonFamilyMembershipInFlight } from '../composables/useFamilyMembership.js'

const route = useRoute()
const router = useRouter()
const retrying = ref(false)

const target = () => safeInternalReturnPath(typeof route.query.to === 'string' ? route.query.to : '', '/top')

const retry = async () => {
  if (retrying.value) return
  retrying.value = true
  // 止まっていた取得を待ち直さず、新しく確認する（指示書 B）
  abandonFamilyMembershipInFlight()
  try {
    await router.replace(target())
  } finally {
    retrying.value = false
  }
}

const reload = () => {
  window.location.assign(target())
}
</script>

<style scoped>
.connection-error {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}
.card {
  max-width: 420px;
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 24px;
  text-align: center;
}
.title {
  font-size: 1.2rem;
  margin: 0 0 12px;
  color: #1f2937;
}
.desc {
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 8px;
}
.note {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 20px;
}
.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.btn {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 1rem;
  border: 1px solid transparent;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-primary {
  background: #3b82f6;
  color: #fff;
}
.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}
</style>
